import Vue from 'vue';
import RevenueAutopilotAPI from '../../api/revenueAutopilot';

const SET_RECORD = 'SET_REVENUE_AUTOPILOT_RECORD';
const SET_DASHBOARD = 'SET_REVENUE_AUTOPILOT_DASHBOARD';
const SET_UI_FLAG = 'SET_REVENUE_AUTOPILOT_UI_FLAG';

const emptyDashboard = () => ({
  mode: 'assisted_review',
  totals: { potential: [], recovered_confirmed: [] },
  opportunities: [],
  recovered: [],
  counts: { eligible: 0, with_value: 0, recovered_confirmed: 0 },
});

export const state = {
  dashboard: emptyDashboard(),
  records: {},
  uiFlags: {
    isFetching: false,
    isMutating: false,
    hasError: false,
    errorMessage: '',
  },
};

export const getters = {
  getDashboard: $state => $state.dashboard,
  getByConversation: $state => id => $state.records[Number(id)] || null,
  getUIFlags: $state => $state.uiFlags,
};

const errorMessage = error =>
  error?.response?.data?.reason ||
  error?.response?.data?.error ||
  'No pudimos completar la acción. Intentá nuevamente.';

export const actions = {
  async getDashboard({ commit }) {
    commit(SET_UI_FLAG, {
      isFetching: true,
      hasError: false,
      errorMessage: '',
    });
    try {
      const { data } = await RevenueAutopilotAPI.getDashboard({ limit: 200 });
      commit(SET_DASHBOARD, data);
      return data;
    } catch (error) {
      commit(SET_UI_FLAG, {
        hasError: true,
        errorMessage: errorMessage(error),
      });
      throw error;
    } finally {
      commit(SET_UI_FLAG, { isFetching: false });
    }
  },

  async getConversation({ commit }, conversationId) {
    commit(SET_UI_FLAG, {
      isFetching: true,
      hasError: false,
      errorMessage: '',
    });
    try {
      const { data } = await RevenueAutopilotAPI.getDashboard({
        conversation_id: conversationId,
        limit: 1,
      });
      const record = data.opportunities?.[0] || data.recovered?.[0] || null;
      commit(SET_RECORD, { conversationId, record });
      return record;
    } catch (error) {
      commit(SET_UI_FLAG, {
        hasError: true,
        errorMessage: errorMessage(error),
      });
      throw error;
    } finally {
      commit(SET_UI_FLAG, { isFetching: false });
    }
  },

  async mutateConversation(
    { commit, dispatch },
    { conversationId, operation }
  ) {
    commit(SET_UI_FLAG, {
      isMutating: true,
      hasError: false,
      errorMessage: '',
    });
    try {
      await operation();
      return await dispatch('getConversation', conversationId);
    } catch (error) {
      commit(SET_UI_FLAG, {
        hasError: true,
        errorMessage: errorMessage(error),
      });
      throw error;
    } finally {
      commit(SET_UI_FLAG, { isMutating: false });
    }
  },

  analyze({ dispatch }, conversationId) {
    return dispatch('mutateConversation', {
      conversationId,
      operation: () => RevenueAutopilotAPI.analyze(conversationId),
    });
  },
  prepare({ dispatch }, conversationId) {
    return dispatch('mutateConversation', {
      conversationId,
      operation: () => RevenueAutopilotAPI.prepare(conversationId),
    });
  },
  approve({ dispatch }, { conversationId, followUpId }) {
    return dispatch('mutateConversation', {
      conversationId,
      operation: () => RevenueAutopilotAPI.approve(followUpId),
    });
  },
  updateDraft({ dispatch }, { conversationId, followUpId, proposedMessage }) {
    return dispatch('mutateConversation', {
      conversationId,
      operation: () =>
        RevenueAutopilotAPI.updateDraft(followUpId, proposedMessage),
    });
  },
  discard({ dispatch }, { conversationId, followUpId }) {
    return dispatch('mutateConversation', {
      conversationId,
      operation: () => RevenueAutopilotAPI.discard(followUpId),
    });
  },
};

export const mutations = {
  [SET_RECORD]($state, { conversationId, record }) {
    Vue.set($state.records, Number(conversationId), record);
  },
  [SET_DASHBOARD]($state, data) {
    $state.dashboard = data || emptyDashboard();
    [
      ...($state.dashboard.opportunities || []),
      ...($state.dashboard.recovered || []),
    ].forEach(record =>
      Vue.set($state.records, Number(record.conversation_id), record)
    );
  },
  [SET_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
};

export default { namespaced: true, state, getters, actions, mutations };
